import { Component, EventEmitter, Inject, Input, Output, SimpleChanges } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { FormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { EditablePostClientStatus } from '../../../../core/enums/editable-post-client-status';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MachineWorkDto } from '../../../../core/models/machines/machine-work-dto';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { DATE_FORMATS } from '../../../../../app/core/config/date-formats';
import { AuthService } from '../../../../core/services/auth.service';
import { AuthPolicy } from '../../../../core/enums/auth-policy';
import moment from 'moment';

@Component({
  selector: 'app-machine-work-hours',
  imports: [MatTableModule, MatPaginatorModule, CommonModule,
    MatTabsModule, FormsModule, MatFormField, MatInputModule,
    MatIcon, MatCheckboxModule, MatButtonModule, MatTooltipModule, MatDatepickerModule, MatNativeDateModule],
  templateUrl: './machine-work-hours.component.html',
  styleUrl: './machine-work-hours.component.scss',
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'sv-SE' }
  ]
})
export class MachineWorkHoursComponent {
  @Input() machineWorkHours: MachineWorkDto[] = [];
  @Output() machineChanged = new EventEmitter<boolean>();
  @Output() machineWorkHoursValidates = new EventEmitter<boolean>();

  newRowDefaultValues = {
    from: undefined,
    to: undefined,
    hoursPerDay: undefined,
    clientStatus: EditablePostClientStatus.New,
    tempId: undefined
  };

  machineWorkHoursDataSource = new MatTableDataSource<MachineWorkDto>();
  displayedColumns = ["from-c", "to-c", "hoursPerDay-c"];
  clientStatus = EditablePostClientStatus;
  newRow: MachineWorkDto = { ...this.newRowDefaultValues };
  validationErrors: { [key: string]: { from?: string; to?: string; hoursPerDay?: string } } = {};
  isPolicyPt: boolean = false;
  isReadonly: boolean = true;
  noActivePeriodsError: boolean = false;

  constructor(@Inject(MAT_DATE_FORMATS) private formats: MatDateFormats,
    private authService: AuthService) {
  }

  ngOnInit() {
    this.isPolicyPt = this.authService.isPolicy(AuthPolicy.PT);

    if (this.isPolicyPt) {
      this.displayedColumns.push("action-c");
    }
    this.isReadonly = !this.authService.isPolicyPT();
    if (!this.machineHasActiveWorkHours()) {
      this.noActivePeriodsError = true;
      this.machineChanged.emit(false);
      this.machineWorkHoursValidates.emit(false);

      return;
    }

    this.machineWorkHoursValidates.emit(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['machineWorkHours']) {
      this.refreshDataSource();
    }
  }

  onRowChanged(row: MachineWorkDto) {
    if (!this.validateMachineWorkHours(row)) {
      this.machineChanged.emit(true);
      this.machineWorkHoursValidates.emit(false);

      return;
    }

    if (row.from) {
      row.from = moment(row.from).format('YYYY-MM-DD');
    }

    if (row.to) {
      row.to = moment(row.to).format('YYYY-MM-DD');
    }

    if (row.id && row.clientStatus !== EditablePostClientStatus.New) {
      row.clientStatus = EditablePostClientStatus.Updated;
    }

    this.machineChanged.emit(true);
    this.machineWorkHoursValidates.emit(true);
    this.refreshDataSource();
  }

  onNewRowChanged(row: MachineWorkDto) {
    this.machineWorkHoursValidates.emit(false);

    if (!this.validateMachineWorkHours(row)) {
      this.machineChanged.emit(true);
      return;
    }
  }

  removeRow(row: MachineWorkDto, _index: number) {
    if (row.id) {
      row.clientStatus = EditablePostClientStatus.Deleted;
      this.machineChanged.emit(true);
      this.machineWorkHoursValidates.emit(true);
    } else {
      const machineWorkIndex = this.machineWorkHours.indexOf(row);
      if (machineWorkIndex > -1) {
        this.machineWorkHours.splice(machineWorkIndex, 1);
      }
    }

    const activeRows = this.machineWorkHours.filter(r =>
      r.clientStatus !== EditablePostClientStatus.Deleted
    );
    const activePeriods = activeRows.filter(r => !r.to);
    if (activePeriods.length < 1) {
      this.noActivePeriodsError = true;
      this.machineWorkHoursValidates.emit(false);
      this.machineChanged.emit(true);
    }

    this.refreshDataSource();
  }

  newRowValidates(): boolean {
    return !this.newRowIsEmpty() && this.validateMachineWorkHours(this.newRow);
  }

  clearNewRow() {
    this.newRow = { ...this.newRowDefaultValues };
  }

  addNewRow() {
    this.newRow.from = this.newRow.from ? moment(this.newRow.from).format('YYYY-MM-DD') : undefined;
    this.newRow.to = this.newRow.to ? moment(this.newRow.to).format('YYYY-MM-DD') : undefined;
    this.newRow.tempId = 'localId' + Date.now();
    const rowToAdd = { ...this.newRow } as MachineWorkDto;
    this.machineWorkHours.push(rowToAdd);
    this.refreshDataSource();
    this.clearNewRow();
    this.machineChanged.emit(true);
    this.machineWorkHoursValidates.emit(true);
  }

  newRowIsEmpty(): boolean {
    if (!this.newRow.from || !this.newRow.hoursPerDay) {
      return true;
    }

    return false;
  }

  clearToDate(element: any) {
    element.to = null;
    this.onRowChanged(element);
  }

  clearNewToDate() {
    this.newRow.to = undefined;
  }

  private validateMachineWorkHours(newMachineWork: MachineWorkDto): boolean {
    const key = newMachineWork.id || newMachineWork.tempId || 'newRow';
    let hasValidationErrors = false;

    if (this.validationErrors[key]) {
      this.clearValidationErrorsForRow(key);
    }

    this.validationErrors[key] = {};

    const machineWorkHours = this.machineWorkHours.filter(r =>
      r.clientStatus !== EditablePostClientStatus.Deleted
    );

    if (!newMachineWork.to) {
      const rowsWithUndefinedTo = machineWorkHours.filter(r => !r.to);
      const otherRowsWithUndefinedTo = rowsWithUndefinedTo.filter(r => r !== newMachineWork);
      if (otherRowsWithUndefinedTo.length > 0) {
        this.validationErrors[key].to = 'Det finns redan en aktiv period (utan slutdatum)';
        hasValidationErrors = true;
      }
    }

    if (this.isMachineWorkOverlapping(newMachineWork, machineWorkHours)) {
      this.validationErrors[key].from = 'Perioden överlappar med en annan rad';
      this.validationErrors[key].to = 'Perioden överlappar med en annan rad';
      hasValidationErrors = true;
    }

    if (newMachineWork.hoursPerDay! <= 0 || newMachineWork.hoursPerDay! > 24) {
      this.validationErrors[key].hoursPerDay = 'Timmar måste vara mellan 1-24';
      hasValidationErrors = true;
    }

    this.noActivePeriodsError = false;
    if (!this.machineHasActiveWorkHours() && newMachineWork.to != null) {
      this.noActivePeriodsError = true;
      hasValidationErrors = true;
    }

    return hasValidationErrors ? false : true;
  }

  private isMachineWorkOverlapping(machineWork: MachineWorkDto, machineWorkHours: MachineWorkDto[]) {
    let hasValidationErrors = false;

    const otherMachineWorkHours = machineWorkHours.filter(r => r !== machineWork && r.from);
    const currentFrom = moment(machineWork.from);
    const currentTo = moment(machineWork.to);

    for (const otherMachineWork of otherMachineWorkHours) {
      const otherFrom = moment(otherMachineWork.from);
      const otherTo = otherMachineWork.to ? moment(otherMachineWork.to) : null;
      const otherMachineWorkHasPeriodWithoutEndDate = !otherTo;

      let hasOverlap = false;

      if (otherFrom && otherTo) {
        hasOverlap = currentFrom.isSameOrBefore(otherTo) && currentTo.isSameOrAfter(otherFrom);
      }

      if (otherMachineWorkHasPeriodWithoutEndDate) {
        hasOverlap = currentTo.isSameOrAfter(otherFrom);
      }

      if (!machineWork.to && otherFrom) {
        hasOverlap = currentFrom.isSameOrBefore(otherFrom);
      }

      if (hasOverlap) {
        hasValidationErrors = true;
        break;
      }
    }

    return hasValidationErrors;
  }

  private clearValidationErrorsForRow(key: string | number) {
    this.validationErrors[key].from = '';
    this.validationErrors[key].to = '';
    this.validationErrors[key].hoursPerDay = '';
  }

  private machineHasActiveWorkHours(): boolean {
    const workHours = this.machineWorkHours.filter(r =>
      r.clientStatus !== EditablePostClientStatus.Deleted
    );
    const today = moment().startOf('day');
    const activePeriods = workHours.filter(r => moment(r.from).startOf('day').isSameOrBefore(today) && !r.to);

    return activePeriods.length === 1;
  }

  private refreshDataSource(): void {
    if (!this.machineWorkHoursDataSource) {
      return;
    }

    const sortedWorkHours = [...(this.machineWorkHours ?? [])].sort((a, b) => {
      if (!a.to && b.to) {
        return -1;
      }

      if (!b.to && a.to) {
        return 1;
      }

      if (!a.from && !b.from) {
        return 0;
      }

      if (!a.from) {
        return 1;
      }

      if (!b.from) {
        return -1;
      }

      return new Date(a.from).getTime() - new Date(b.from).getTime();
    });

    this.machineWorkHoursDataSource.data = sortedWorkHours;
    this.machineWorkHoursDataSource._updateChangeSubscription();
  }
}
